'use client';

import React, { useCallback } from 'react';
import { LayoutDashboard } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Card from '@/src/components/features/property/ui/Card';
import { LucideIcon } from '@/src/components/LucideIcon';
import { TotalMonthlyPriceIndicator } from '@/src/components/features/listings/TotalMonthlyPrice';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { UnitTable } from './UnitTable';
import type { Floorplan, Unit } from './UnitTable';
import { getFloorplanCardImageUrl } from './floorplanMedia';
import { FloorPlanImageViewer } from './FloorPlanImageViewer';
import * as styles from './unitsModule.styles';

type HdpT = ReturnType<typeof useTranslations<'hdp'>>;

const MAX_PREVIEW_ROWS = 5;

type FloorPlanCardProps = {
  floorplan: Floorplan;
  listing: PropertyDetailListing;
  onUnitSelect: (unit: Unit, floorplan: Floorplan) => void;
  onSeeAll: (floorplan: Floorplan) => void;
  showImageArea: boolean;
  compact?: boolean;
};

const getBedroomLabel = (beds: number, t: HdpT): string => {
  if (beds === 0) return t('studioLabel');
  if (beds === 1) return t('oneBed');
  return t('nBeds', { count: beds });
};

const FloorPlanPlaceholderIcon = () => <LucideIcon icon={LayoutDashboard} size="lg" />;

const NoFloorPlanPlaceholder = ({ t }: { t: HdpT }) => (
  <div className={styles.floorPlanImageOuter}>
    <div className={styles.noFloorPlanPlaceholder}>
      <FloorPlanPlaceholderIcon />
      <span className={styles.placeholderText}>{t('noFloorPlan')}</span>
      <span className={styles.placeholderSubtext}>{t('noFloorPlanSubtext')}</span>
    </div>
  </div>
);

const FloorPlanSummary = ({ floorplan, t }: { floorplan: Floorplan; t: HdpT }) => {
  const parts: Array<string> = [];
  parts.push(getBedroomLabel(floorplan.beds, t));
  if (floorplan.baths) parts.push(t('nBath', { count: floorplan.baths }));
  if (floorplan.sqft) parts.push(t('sqftValue', { value: floorplan.sqft }));

  return (
    <p className={styles.floorPlanSummary}>
      {parts.map((part, i) => (
        <React.Fragment key={part}>
          {i > 0 && <span className={styles.floorPlanSummarySeparator}> {'\u2022'} </span>}
          {part}
        </React.Fragment>
      ))}
    </p>
  );
};

export const FloorPlanCard = React.memo(
  ({ floorplan, listing, onUnitSelect, onSeeAll, showImageArea, compact }: FloorPlanCardProps) => {
    const t = useTranslations('hdp');
    const imageUrl = getFloorplanCardImageUrl(floorplan);

    const handleSeeAll = useCallback(() => {
      onSeeAll(floorplan);
    }, [onSeeAll, floorplan]);

    const compactCardClassName = showImageArea ? styles.compactCard : styles.compactCardWithoutImage;

    return (
      <Card variant="default" size="md" className={compact ? compactCardClassName : styles.baseCard}>
        {showImageArea &&
          (imageUrl ? (
            <div className={styles.floorPlanImageOuter}>
              <div className={compact ? styles.compactImageWrapper : styles.floorPlanImageWrapper}>
                <FloorPlanImageViewer
                  src={imageUrl}
                  alt={t('floorPlanAlt', { name: floorplan.name || '' })}
                  buttonLabel={
                    floorplan.name ? t('viewFloorPlan', { name: floorplan.name }) : t('viewFloorPlanGeneric')
                  }
                  imageClassName={styles.floorPlanImage}
                />
              </div>
            </div>
          ) : (
            <NoFloorPlanPlaceholder t={t} />
          ))}

        <div className={styles.cardContent}>
          <div className={styles.floorPlanSummaryRow}>
            <FloorPlanSummary floorplan={floorplan} t={t} />
            {listing.hasTotalMonthlyPrice !== true && (
              <TotalMonthlyPriceIndicator
                listing={listing}
                floorplanId={floorplan.modelId}
                className={styles.floorPlanFeesBadge}
                feesTestId="floorplan-fees"
              />
            )}
          </div>

          <div className={compact ? styles.compactUnitTableWrapper : undefined}>
            <UnitTable
              units={floorplan.units}
              floorplan={floorplan}
              listing={listing}
              onUnitSelect={onUnitSelect}
              maxRows={MAX_PREVIEW_ROWS}
              onSeeAll={handleSeeAll}
            />
          </div>
        </div>
      </Card>
    );
  },
);

FloorPlanCard.displayName = 'FloorPlanCard';
